<template>
  <div class="tw-px-2">
    <PageHeader>Dashboard
      <template #right v-if="!isMobile">
        <v-row class="mr-5">
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" v-if="!isEditing" right @click="editDashboard">
                Configure
              </v-btn>
              <v-btn v-else right @click="saveDashboard"> Save </v-btn>
            </template>
            <span>Drag dashboard widgets to desired location
              (widgets with moving lines are draggable). Select the Displayed Widgets
              dropdown to choose which widgets you would like to display.</span>
          </v-tooltip>
        </v-row>
      </template>
    </PageHeader>
    <PageLoader ref="loader">
      <v-row v-if="isEditing" class="justify-center">
        <v-col cols="2">
          <h3 class="text-center"> Displayed Widgets </h3>
          <v-select class="displayedWidgets"
           attach multiple :items="initialWidgets" v-model="displayedWidgets" />
        </v-col>
      </v-row>
      <hr />
      <template v-if="data !== null">
        <MerchantDashboardRow
          @refreshDashboard="loadDashboard"
          :data="data"
          v-for="row in data.dashboardConfig"
          :columns="row.columns"
          :key="row.key"
          :activityPeriod="activityPeriod"
          :isEditing="isEditing"
        />
      </template>
      <template #error>
        There was a problem loading the dashboard data. Please refresh the page and try again.
      </template>
    </PageLoader>
    <RestrictedAccountModal
      v-if="data !== null"
      :showModal="showAccountModal"
      @closeRestrictedAccountModal="closeRestrictedAccountModal"
      :iso="data.iso"
    />
    <LoadingModal loadingText="Saving dashboard..." :showModal="isSubmitting" />
  </div>
</template>

<script>
import { isUndefined } from 'lodash';
import api from '@/common/api';
import PageLoader from '@/common/components/PageLoader.vue';
import PageHeader from '@/common/components/PageHeader.vue';
import RestrictedAccountModal from '@/common/components/modals/RestrictedAccountModal.vue';
import { MerchantStatus } from '@/iso/views/merchants/merchants.types';
import LoadingModal from '@/common/components/LoadingModal.vue';
import MerchantDashboardRow from './merchant-dashboard/MerchantDashboardRow.vue';


export default {
  title: 'Dashboard',
  mounted() {
    this.loadDashboard();
  },
  data() {
    return {
      activityPeriod: 'thisMonth',
      data: null,
      showAccountModal: false,
      isEditing: false,
      isSubmitting: false,
    };
  },
  methods: {
    submitActions(isSubmitting) {
      this.isSubmitting = isSubmitting;
    },
    loadDashboard() {
      api
        .get('/dashboard')
        .then(({ data }) => {
          this.data = data;
          if (this.$refs.loader) {
            this.$refs.loader.setReady();
          }
          if (
            !isUndefined(this.data.merchantStatus)
            && this.data.merchantStatus === MerchantStatus.RESTRICTED
          ) {
            this.showAccountModal = true;
          }
        })
        .catch(() => {
          if (this.$refs.loader) {
            this.$refs.loader.setError();
          }
        });
    },
    editDashboard() {
      this.isEditing = true;
    },
    saveDashboard() {
      this.isEditing = false;
      this.submitActions(true);
      api
        .patch(`/edit-dashboard/${this.data.dashboardId}`, this.data)
        .then(() => {
          this.showSuccess();
        })
        .catch(() => {
          if (this.$refs.loader) {
            this.$refs.loader.setError();
          }
        });
    },
    showSuccess() {
      this.submitActions(false);
      this.$toasted.success('Dashboard saved successfully!');
    },
    closeRestrictedAccountModal() {
      this.showAccountModal = false;
    },
  },
  computed: {
    isMobile() {
      if (this.$vuetify.breakpoint.width === 0) return false;
      return this.$vuetify.breakpoint.width < 600;
    },
    displayedWidgets: {
      get() {
        return this.existingWidgets;
      },
      set(newVal) {
        const selectedWidgets = this.initialWidgets.filter(
          (item) => newVal.includes(item.value),
        );

        this.data.dashboardConfig
          .map((rows) => rows.columns.map((cols) => cols.fields
            .forEach((field) => {
              let seen = false;
              selectedWidgets.forEach((selectedWidget) => {
                if (selectedWidget.value === field.value) {
                  seen = true;
                }
              });
              if (!seen) {
              // eslint-disable-next-line
                field.hidden = true;
              } else {
              // eslint-disable-next-line
                field.hidden = false;
              }
            })));
      },
    },
    // all possible widgets
    initialWidgets() {
      const leftArray = this.data.defaultDashboardConfig[0].columns[0].fields;
      const rightArray = this.data.defaultDashboardConfig[0].columns[1].fields;
      const bothArrays = leftArray.concat(rightArray);
      return bothArrays;
    },
    // non-hidden widgets
    existingWidgets() {
      const leftArray = this.data.dashboardConfig[0].columns[0].fields;
      const rightArray = this.data.dashboardConfig[0].columns[1].fields;
      const combinedArray = leftArray.concat(rightArray);
      const filteredCombinedArray = combinedArray.filter((field) => field.hidden === false);
      return filteredCombinedArray;
    },
  },
  components: {
    PageLoader,
    PageHeader,
    RestrictedAccountModal,
    MerchantDashboardRow,
    LoadingModal,
  },
};
</script>
